import React from "react";
import { ArrowLeft, ArrowRight, Check, LockKeyhole } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useCourseModules } from "../../contexts/CourseContext";
import { usePageTitle } from "../../hooks/usePageTitle";
import { getLastModule } from "../../utils/courseLocalProgress";
import { CoursePageSkeleton } from "../../components/skeletons/PageSkeletons";
import "./course.css";

const CoursePage: React.FC = () => {
  const { courseSlug = "" } = useParams();
  const { courses, chapters, courseModules, loading, error } = useCourseModules();

  const course = courses.find((c) => c.slug === courseSlug);
  const courseChapters = chapters
    .filter((c) => c.courseSlug === courseSlug && c.published)
    .sort((a, b) => a.order - b.order);
  const courseModulesForSlug = courseModules.filter((m) => m.courseSlug === courseSlug);

  usePageTitle(course?.title || "Course");

  if (loading) {
    return (
      <CoursePageSkeleton />
    );
  }

  if (error && courses.length === 0) {
    return (
      <div className="course-shell course-loading" role="alert">
        <p className="course-wrap">The curriculum could not be loaded right now. Please refresh.</p>
      </div>
    );
  }

  if (!course || course.status !== "published") {
    return <Navigate to="/writing" replace />;
  }

  const writtenCount = courseModulesForSlug.filter((m) => m.published).length;
  const readableModules = courseChapters
    .flatMap((ch) => courseModulesForSlug.filter((m) => m.chapterSlug === ch.slug && m.published))
    .sort((a, b) => a.order - b.order);
  const lastSlug = getLastModule(courseSlug);
  const resumeModule = readableModules.find((m) => m.slug === lastSlug);
  const ctaModule = resumeModule || readableModules[0];

  return (
    <div className="course-shell">
      <section className="course-hero course-hero--compact" aria-labelledby="course-title">
        <div className="course-wrap">
          <Link className="course-back-link" to="/writing">
            <ArrowLeft aria-hidden="true" size={17} /> All writing
          </Link>

          <div className={`course-hero__row${course.coverImageUrl ? " course-hero__row--with-cover" : ""}`}>
            <div className="course-hero__copy">
              <p className="course-eyebrow">{course.title}</p>
              <h1 id="course-title">{course.tagline}</h1>
              <p className="course-hero__promise">{course.description}</p>

              <div className="course-hero__cta">
                {ctaModule && (
                  <Link className="course-button course-button--primary" to={`/writing/series/${courseSlug}/${ctaModule.slug}`}>
                    {resumeModule ? "Continue where you left off" : "Start reading"} <ArrowRight aria-hidden="true" size={18} />
                  </Link>
                )}
                <p className="course-honesty-note">
                  {writtenCount} published {writtenCount === 1 ? "chapter" : "chapters"}
                </p>
              </div>
            </div>

            {course.coverImageUrl && (
              <div className="course-hero__cover" aria-hidden="true">
                <img src={course.coverImageUrl} alt="" />
              </div>
            )}
          </div>
        </div>
      </section>

      <main>
        <section className="course-section course-wrap" id="curriculum" aria-label="Chapters">
          {courseChapters.map((chapterItem) => {
            const modulesForChapter = courseModulesForSlug
              .filter((m) => m.chapterSlug === chapterItem.slug)
              .sort((a, b) => a.order - b.order);

            return (
              <div key={chapterItem.slug} className="course-chapter-group">
                <div className="course-chapter-group__heading">
                  <span className="course-chapter-group__number" aria-hidden="true">{chapterItem.number}</span>
                  <div className="course-chapter-group__intro">
                    <p className="course-chapter-group__eyebrow">Chapter {chapterItem.number}</p>
                    <h3>{chapterItem.title}</h3>
                    <p className="course-chapter-group__question">{chapterItem.centralQuestion}</p>
                    {chapterItem.summary && <p className="course-chapter-group__summary">{chapterItem.summary}</p>}
                    <div className="course-chapter-group__meta">
                      <span>{chapterItem.readingTime} min</span>
                      <span>{chapterItem.difficulty}</span>
                      <span>{modulesForChapter.length} {modulesForChapter.length === 1 ? "module" : "modules"}</span>
                    </div>
                  </div>
                </div>

                <ol className="course-module-list">
                  {modulesForChapter.map((moduleItem) => (
                    <li key={moduleItem.slug} className={`course-module ${moduleItem.published ? "course-module--written" : "course-module--planned"}`}>
                      {moduleItem.published && (
                        <Link
                          className="course-module__row-link"
                          to={`/writing/series/${courseSlug}/${moduleItem.slug}`}
                          aria-label={`Read ${moduleItem.title}`}
                        />
                      )}
                      <span className="course-module__number" aria-hidden="true">{moduleItem.number}</span>
                      <div className="course-module__body">
                        <div className="course-module__meta">
                          <span>{moduleItem.published ? "Published" : "Not published"}</span>
                        </div>
                        <h4>{moduleItem.title}</h4>
                      </div>
                      <div className="course-module__action">
                        {moduleItem.published ? (
                          <span aria-hidden="true">
                            Read <ArrowRight aria-hidden="true" size={17} />
                          </span>
                        ) : (
                          <span aria-label={`${moduleItem.title} is not available yet`}>
                            <LockKeyhole aria-hidden="true" size={16} /> Not open yet
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            );
          })}
        </section>

        <section className="course-section course-wrap course-boundaries" aria-labelledby="boundaries-title">
          <div className="course-section__heading">
            <p>Deliberate boundaries</p>
            <h2 id="boundaries-title">This is not a framework certification.</h2>
          </div>
          <div className="course-boundaries__grid">
            <p><Check aria-hidden="true" /> Chapters publish as they are written and reviewed, not on a promotional schedule.</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CoursePage;
